import { Enums } from "./thor.enums";
import { CategoryField, FieldObject } from "./typings/categories";

export namespace Constants {
    export const DEFAULT_LOCAL_SETTINGS = {
        imageQuality: Enums.ImageQuality.Maximum,
        handleExif: true
    };

    export const IMAGE_RES = {};
    IMAGE_RES[Enums.ImageQuality.Maximum] = { width: 3840, height: 2160 };
    IMAGE_RES[Enums.ImageQuality.High] = { width: 2560, height: 1440 };
    IMAGE_RES[Enums.ImageQuality.Medium] = { width: 1920, height: 1080 };
}

export const COMPANY = {
    name: "Commissaires-Priseurs Multimédia",
    address: "14 rue des Pyramides, 75001 PARIS",
    rcs: "B 437 868 425"
};

export const CONTACT_EMAIL_BUSINESS = {
    [Enums.Site.Interencheres]: "commercial@interencheres.com",
    [Enums.Site.Auction]: "commercial@auction.fr"
};

export const CONTACT_EMAIL = {
    [Enums.Site.Interencheres]: "support@interencheres.com",
    [Enums.Site.Auction]: "support@auction.fr"
};

export const EXTENSION_DOWNLOAD_PATH = "https://chrome.google.com/webstore/detail/";

export const FIELDS: FieldObject[] = [
    { key: CategoryField.MOA, i18n: "meubles_et_objets_d_art", premium: true },
    { key: CategoryField.MAT, i18n: "materiels_professionnels" },
    { key: CategoryField.VEH, i18n: "vehicules", premium: true }
];

export const LOCALE = Enums.Locale.Fr;

export const LOCALE_ISO = {
    [Enums.Locale.En]: { iso: "en-GB" },
    [Enums.Locale.Es]: { iso: "es-ES" },
    [Enums.Locale.Fr]: { iso: "fr-FR" },
    [Enums.Locale.It]: { iso: "it-IT" }
};

export const DATE_LOCALE = LOCALE;
export const DATE_FORMAT_ISO = "YYYY-MM-DDTHH:mm:ssZ";
export const DATE_TIME_FORMAT_SLUG = "YYYYMMDD-HHmm";

export const ONLINE_SALE_DURATION = 7;
export const ONLINE_SALE_START_HOUR = 20;

export const ONLINE_MOMENT = {
    hour: ONLINE_SALE_START_HOUR,
    minute: 0,
    second: 0,
    millisecond: 0
};

export const COMMISSION_FEES_PCT = {
    judicial_before_oct_2023: 3,
    judicial: 1,
    voluntary: 3
};

export const MAX_ONLINE_ITEMS = 360;

export const ITEM_DESCRIPTION_MIN_LENGTH = 10;

export const VEHICLES_FEES = {
    before_oct_2023: {
        [Enums.Currency.CHF]: 35,
        [Enums.Currency.EUR]: 35,
        [Enums.Currency.GBP]: 30,
        [Enums.Currency.USD]: 40
    },
    current: {
        [Enums.Currency.CHF]: 39,
        [Enums.Currency.EUR]: 40,
        [Enums.Currency.GBP]: 35,
        [Enums.Currency.USD]: 43
    }
};

export const TAXES_MULT = 1.2; // VAT 20%

export const MAX_CARD_AMOUNT = 10000;

/* link_payment deadlines :
 * LINK_PAYMENT_RELAUNCH_MIN: relaunch action available 7 days after link is created
 * LINK_PAYMENT_REACTIVATE_MIN: reactivate available 15 days after link is created or relaunched
 * AFTER_SALE_PAYMENT_MAX: link_payment unavailable 40 days after sale is past
 */
export const LINK_PAYMENT_RELAUNCH_MIN = 7;
export const LINK_PAYMENT_REACTIVATE_MIN = 15;
export const AFTER_SALE_PAYMENT_MAX = 40;

export const SITE_NAME = {
    [Enums.Site.Auction]: "Auction",
    [Enums.Site.Interencheres]: "Interencheres"
};

export const DOMAIN = {
    [Enums.Site.Auction]: "auction.fr",
    [Enums.Site.Interencheres]: "interencheres.com"
};

export const I18N_KEY_MISSING = "key_missing";

export const V2_BASE_PATH = "/v2/";

export const STATE = {
    SALES: {
        EDIT: {
            ABSTRACT: "thor.sale",
            INFORMATION: "thor.sale.information",
            LOCATION: "thor.sale.location",
            CONDITIONS: "thor.sale.conditions",
            SHIPPING: "thor.sale.shipping",
            LIVE: "thor.sale.live",
            ITEMS: "thor.sale.items",
            BIDS: "thor.sale.bids",
            SUBSCRIPTIONS: "thor.sale.subscriptions",
            PAYMENTS: "thor.sale.payments",
            STATS: "thor.sale.stats"
        }
    },
    ONLINE: {
        EDIT: {
            ABSTRACT: "thor.online-container",
            INFORMATION: "thor.online-container.information",
            LOCATION: "thor.online-container.location",
            CONDITIONS: "thor.online-container.conditions",
            SHIPPING: "thor.online-container.shipping",
            ONLINE: "thor.online-container.online",
            ITEMS: "thor.online-container.items",
            SUBSCRIPTIONS: "thor.online-container.subscriptions",
            STATS: "thor.online-container.stats"
        }
    },
    ORGANIZATION: {
        ABSTRACT: "thor.main.organization",
        MAIN: "thor.main.organization.main-data",
        MY_PAGE: "thor.main.organization.my-page",
        AUTHORIZATION: "thor.main.organization.authorization"
    },
    ERROR: {
        MAIN: "error",
        MAINTENANCE: "maintenance",
        403: "403",
        404: "404"
    },
    LOKI: {
        ABSTRACT: "thor.loki",
        ORGANIZATIONS: "thor.loki.organizations",
        SALES: {
            FUTURE: "thor.loki.future-sales",
            PAST: "thor.loki.past-sales"
        },
        ITEMS: {
            FUTURE: "thor.loki.future-items",
            PAST: "thor.loki.past-items"
        }
    },
    THOR: {
        ABSTRACT: "thor",
        MAIN: "thor.main"
    },
    V2: {
        LOGIN: {
            name: "v2_login",
            url: `${V2_BASE_PATH}login`
        },
        HOME: {
            name: "home",
            url: `${V2_BASE_PATH}`
        },
        LOKI: {
            ORGANIZATIONS: {
                name: "loki_orgnanizations",
                url: `${V2_BASE_PATH}loki`
            },
            PAST_SALES: {
                name: "loki_past_sales",
                url: `${V2_BASE_PATH}loki/past-sales`
            },
            FUTURE_SALES: {
                name: "loki_future_sales",
                url: `${V2_BASE_PATH}loki/future-sales`
            },
            PAST_ITEMS: {
                name: "loki_past_items",
                url: `${V2_BASE_PATH}loki/past-items`
            },
            FUTURE_ITEMS: {
                name: "loki_future_items",
                url: `${V2_BASE_PATH}loki/future-items`
            },
            ITEMS_PREMIUM: {
                name: "loki_items_premium",
                url: `${V2_BASE_PATH}loki/premium?premium_status=PENDING`
            }
        },
        ERROR_404: {
            name: "v2_error_404",
            url: `${V2_BASE_PATH}404`
        },
        MEETUPS: {
            name: "v2_meetups",
            url: `${V2_BASE_PATH}meetups`
        },
        ORGANIZATION: {
            MAIN: {
                name: "thor.main.organization.main-data",
                url: `${V2_BASE_PATH}etude`
            },
            MY_PAGE: {
                name: "v2_organization_my_page",
                url: `${V2_BASE_PATH}organization/my-page`
            },
            SALE: {
                name: "v2_organization_sale",
                url: `${V2_BASE_PATH}organization/sale-parameters`
            },
            AUTHORIZATION: {
                name: "v2_organization_authorization",
                url: `${V2_BASE_PATH}organization/authorizations`
            },
            SALES_CONDITIONS: {
                name: "v2_organization_sales_conditions",
                url: `${V2_BASE_PATH}organization/sales-conditions`
            },
            PAYMENT_CONTRACTS: {
                name: "v2_organization_payment_contracts",
                url: `${V2_BASE_PATH}organization/payment-contracts`
            },
            PREFERENCES: {
                name: "v2_organization_preferences",
                url: `${V2_BASE_PATH}organization/preferences`
            }
        },
        PAYOUTS: {
            MAIN: {
                name: "payouts",
                url: `${V2_BASE_PATH}payouts`
            }
        },
        SALES: {
            FUTURE: {
                name: "v2_future_sales",
                url: `${V2_BASE_PATH}sales/future`
            },
            PAST: {
                name: "v2_past_sales",
                url: `${V2_BASE_PATH}sales/past`
            },
            PROMPTER: {
                name: "v2_prompter",
                url: `${V2_BASE_PATH}prompter`
            },
            SALE: {
                INFO: {
                    name: "v2_sale_info",
                    url: saleId => `${V2_BASE_PATH}sales/${saleId}`
                },
                BIDS: {
                    name: "v2_sale_bids",
                    url: saleId => `${V2_BASE_PATH}sales/${saleId}/bids`
                },
                LIVE: {
                    name: "v2_sale_live",
                    url: saleId => `${V2_BASE_PATH}sales/${saleId}/live`
                },
                ONLINE: {
                    name: "v2_sale_online",
                    url: saleId => `${V2_BASE_PATH}sales/${saleId}/online`
                },
                ITEMS: {
                    name: "v2_sale_items",
                    url: saleId => `${V2_BASE_PATH}sales/${saleId}/lots`
                },
                SUBSCRIPTIONS: {
                    name: "v2_sale_subscriptions",
                    url: (saleId, auctionType) => `${V2_BASE_PATH}sales/${saleId}/subscriptions/${auctionType}`
                },
                LOCATION: {
                    name: "v2_sale_address",
                    url: saleId => `${V2_BASE_PATH}sales/${saleId}/address`
                },
                FEES_AND_CONDITIONS: {
                    name: "v2_sale_fees_and_conditions",
                    url: saleId => `${V2_BASE_PATH}sales/${saleId}/fees-and-conditions`
                },
                PAYMENTS: {
                    name: "v2_sale_payments",
                    url: saleId => `${V2_BASE_PATH}sales/${saleId}/payments`
                },
                SHIPPING: {
                    name: "v2_sale_shiping",
                    url: saleId => `${V2_BASE_PATH}sales/${saleId}/shipping`
                },
                STATS: {
                    name: "v2_sale_stats",
                    url: saleId => `${V2_BASE_PATH}sales/${saleId}/stats`
                }
            }
        },
        SEARCH: {
            name: "search",
            url: `${V2_BASE_PATH}base-adjudications`
        },
        STATS: {
            FUTURE: {
                name: "v2_stats_future",
                url: `${V2_BASE_PATH}stats`
            },
            PAST: {
                name: "v2_stats_past",
                url: `${V2_BASE_PATH}stats-ventes-passees`
            }
        },
        STATS_ACTIVITY: {
            name: "v2_stats_activity",
            url: `${V2_BASE_PATH}stats-activity`
        }
    }
};

export const V2_STATES_REDIRECT = [
    STATE.V2.LOGIN,
    STATE.V2.HOME,
    STATE.V2.LOKI.ORGANIZATIONS,
    STATE.V2.LOKI.PAST_SALES,
    STATE.V2.LOKI.FUTURE_SALES,
    STATE.V2.LOKI.PAST_ITEMS,
    STATE.V2.LOKI.FUTURE_ITEMS,
    STATE.V2.LOKI.ITEMS_PREMIUM,
    STATE.V2.SEARCH,
    STATE.V2.ERROR_404
];

export const PREMIUM_DESCRIPTION_MIN_LENGTH = 50;
